import { SelectProps, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ClosureWarningIcon from 'components/Icons/ClosureWarningIcon';
import NoClosureWarningIcon from 'components/Icons/NoClosureWarningIcon';
import Loading from 'components/Loading';
import { AppContext } from 'context';
import { Routes } from 'routes';
import { useClosureById, useClosureRequirements } from 'hooks';
import { colors } from 'theme';
import { ClosureForm, SortingCenterContractsRequirement } from 'types';
import { FormStepComponentProps } from '../MultiStepForm';
import { ClosureContext } from '../../../pages/Closures/ClosureClose';
import { ErrorLink } from './ErrorLink';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    alignItems: 'start',
    flexDirection: 'column',
    maxWidth: '50%',
    margin: '0 auto',
  },
  warning: {
    color: colors.darkOrange,
    display: 'flex',
    justifyContent: 'start',
    alignItems: 'center',
    position: 'relative',
  },
  warningIcon: {
    position: 'absolute',
    right: '72px',
  },
  warningText: {
    fontSize: '18px',
    lineHeight: '21px',
    margin: 'auto 0',
    display: 'flex',
    alignItems: 'center',
    height: '78px',
  },
  requirements: {
    fontSize: '16px',
  },
  noWarning: {
    color: colors.primary,
    display: 'flex',
    alignItems: 'center',
  },
  noWarningText: {
    fontSize: '18px',
    lineHeight: '21px',
    marginLeft: '21px',
  },
  noWarningIcon: {
    width: '78px',
    height: '78px',
    flexShrink: 0,
  },
  link: {
    textDecoration: 'underline',
    cursor: 'pointer',
  },
});

interface ContractListProps extends SelectProps {
  title: string;
  contracts: SortingCenterContractsRequirement[];
  activeLinkToClientContract?: boolean;
}
const ContractList: React.FC<ContractListProps> = props => {
  const { title, contracts, activeLinkToClientContract } = props;
  const history = useHistory();
  const classes = useStyles();

  return (
    <>
      <Typography>{title}</Typography>
      <ul>
        {contracts.map((contract: SortingCenterContractsRequirement, index: number) => {
          const clientName = contract.client.shortName;
          const streamName = contract.stream.name;
          const key = `${title}_${clientName}_${streamName}_${index}`.replace(' ', '_');
          if (activeLinkToClientContract) {
            return (
              <li
                className={classes.link}
                onClick={() => history.push(`/referentiels/clients/${contract.client.id}?tabPanelValue=contracts`)}
                key={key}
              >
                {`${clientName}/${streamName}`}
              </li>
            );
          }
          return <li key={key}>{`${clientName}/${streamName}`}</li>;
        })}
      </ul>
    </>
  );
};

const Step1: React.FC<FormStepComponentProps<ClosureForm, ClosureContext>> = ({
  onSubmit,
  formName,
  previousStepData,
}) => {
  const classes = useStyles();
  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const { t } = useTranslation('translation', { keyPrefix: 'closureCloseForm.step1' });

  const { handleSubmit } = useForm<ClosureForm>({
    mode: 'onBlur',
  });

  const closureId = previousStepData.closureId ?? '';
  const [closure] = useClosureById(closureId);

  const year = closure?.year;
  const month = closure?.month;
  const sortingCenterId = sortingCenter?.['@id'].split('/')[3];

  const [requirements, loading] = useClosureRequirements(sortingCenterId, month, year);

  return (
    <>
      <form id={formName} onSubmit={handleSubmit(onSubmit)} className={classes.form}>
        {!year || !month || !sortingCenterId || loading ? (
          <Loading />
        ) : requirements &&
          (requirements['contractsExpiringThisMonth'].length > 0 ||
            requirements['contractsExpiringInThreeMonths'].length > 0 ||
            requirements['contractsWithoutReceptions'].length > 0 ||
            requirements['contractsWithoutReceptionsForTwoMonths'].length > 0 ||
            requirements['contractsEndingAndNotClosedThisMonth'].length > 0) ? (
          <>
            <div className={classes.warning}>
              <ClosureWarningIcon className={classes.warningIcon} />
              <Typography className={classes.warningText}>{t('warning')}</Typography>
            </div>
            <div className={classes.requirements}>
              {requirements['contractsEndingAndNotClosedThisMonth'].length > 0 && (
                <ContractList
                  title={t('contractsEndingAndNotClosedThisMonth')}
                  contracts={requirements['contractsEndingAndNotClosedThisMonth']}
                  activeLinkToClientContract={true}
                />
              )}
              {requirements['contractsExpiringThisMonth'].length > 0 && (
                <ContractList
                  title={t('contractsExpiringThisMonth')}
                  contracts={requirements['contractsExpiringThisMonth']}
                />
              )}
              {requirements['contractsExpiringInThreeMonths'].length > 0 && (
                <ContractList
                  title={t('contractsExpiringInThreeMonths')}
                  contracts={requirements['contractsExpiringInThreeMonths']}
                />
              )}
              {requirements['contractsWithoutReceptions'].length > 0 && (
                <ContractList
                  title={t('contractsWithoutReceptions')}
                  contracts={requirements['contractsWithoutReceptions']}
                  activeLinkToClientContract={true}
                />
              )}
              {requirements['contractsWithoutReceptionsForTwoMonths'].length > 0 && (
                <ContractList
                  title={t('contractsWithoutReceptionsForTwoMonths')}
                  contracts={requirements['contractsWithoutReceptionsForTwoMonths']}
                  activeLinkToClientContract={true}
                />
              )}
            </div>
          </>
        ) : (
          <div className={classes.noWarning}>
            <NoClosureWarningIcon className={classes.noWarningIcon} />
            <Typography className={classes.noWarningText}>{t('noWarning')}</Typography>
          </div>
        )}
      </form>
      <div style={{ textAlign: 'center' }}>
        <ErrorLink to={Routes.Clients} prelinkText={t('checkDates')} linkText={t('contracts')} />
      </div>
    </>
  );
};

export default Step1;
